import axios from 'axios';
import { filterClosedJobs } from '../utils/jobTransformations';
import { getTokenFromCookie, getDecodedToken } from '../utils/tokenHelper';

// Interface for job location
export interface JobLocation {
  jobId: number;
  country: string;
  region: string;
  postalCode: string;
  city: string;
  externalLocationId: string | null;
}

// Interface for division data from API
export interface DivisionData {
  id: number;
  companyId: number;
  name: string;
  default: boolean;
  active: boolean;
}

// Interface for user/recruiter data from API
export interface UserData {
  id: number;
  companyId: number;
  partnerId: number;
  userType: string;
  active: boolean;
  contactInfo: {
    firstName: string;
    lastName: string;
    title: string | null;
    emailAddress: string;
    phoneNumber: string | null;
  };
}

// Interface for job data from API
export interface JobData {
  id: number;
  recruiterId: number;
  divisionId: number;
  companyId: number;
  partnerId: number;
  requisitionName: string;
  title: string;
  description: string;
  companyName: string;
  applyUrl: string;
  requirements: string | null;
  salaryLow: number | null;
  salaryHigh: number | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  contactExtension: string | null;
  anonymousPosting: boolean;
  entryLevel: boolean;
  jobFunction: string | null;
  experience: string | null;
  industry: string | null;
  jobType: string | null;
  duration: string | null;
  travel: string | null;
  education: string | null;
  salaryType: string | null;
  jobOrigin: string;
  status: string;
  created: string;
  updated: string;
  locations: JobLocation[];
}

// Using relative URL path that will go through the proxy
const API_PATH = '/api/v1';

//TO DO: Get company ID from token
// const DEFAULT_COMPANY_ID = 6444;

const decodedToken = getDecodedToken();
const company_id = decodedToken?.companyId ?? 17691;

/**
 * Helper function to fetch data using the React proxy
 * @param endpoint API endpoint path (without base URL)
 * @param options Optional headers for the API call
 * @returns Promise with API response data
 */
const fetchWithFallback = async <T>(endpoint: string, options?: { headers?: any }): Promise<T> => {
  try {
    const proxyUrl = `${API_PATH}${endpoint}`;
    
    const response = await axios.get(proxyUrl, options);
    return response.data;
  } catch (error) {
    console.error(`❌ API call failed for ${endpoint}:`, error);
    throw error;
  }
};

export const JobsService = {
  // Get all jobs for a company
  getJobs: async (companyId: number = company_id): Promise<JobData[]> => {
    const token = getTokenFromCookie();
    const endpoint = `/company/${companyId}/jobs`;
    const jobs = await fetchWithFallback<JobData[]>(endpoint, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return filterClosedJobs(jobs);
  },

  // Get a single job by ID
  getJobById: async (companyId: number, jobId: number): Promise<JobData> => {
    const token = getTokenFromCookie();
    const endpoint = `/company/${companyId}/jobs/${jobId}`;
    return fetchWithFallback<JobData>(endpoint, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  },

  // Get all divisions for a company
  getDivisions: async (companyId: number = company_id): Promise<DivisionData[]> => {
    const token = getTokenFromCookie();
    const endpoint = `/company/${companyId}/division`;
    return fetchWithFallback<DivisionData[]>(endpoint, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  },

  // Get all users/recruiters for a company
  getUsers: async (companyId: number = company_id): Promise<UserData[]> => {
    const token = getTokenFromCookie();
    const endpoint = `/company/${companyId}/user`;
    return fetchWithFallback<UserData[]>(endpoint, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  }
};

export default JobsService; 